import React, { useState } from 'react';
import { Typography, Button, Model } from 'components/atoms';
import './index.scss';

interface SubTask {
  id: string;
  title: string;
  description: string;
  deadline: string;
  status: 'Completed' | 'Pending' | 'In Progress';
  priority: 'High' | 'Medium' | 'Low';
}

interface Review {
  id: string;
  comment: string;
  rating: number;
  reviewedAt: string;
  reviewedBy: string;
}

interface GoalReview {
  id: string;
  goalAssignmentGoalId: string;
  reviewerId: string;
  reviewerName: string;
  reviewDate: string;
  reviewText: string;
  rating: number;
  reviewerRole: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'Completed' | 'Pending' | 'In Progress';
  reviews: Review[];
  subtasks: SubTask[];
  goalAssignmentGoalId?: string;
}

interface TaskDetailsModalProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onAddSubtask: (taskId: string, subtask: Omit<SubTask, 'id'>) => void;
  onStatusChange: (taskId: string, status: Task['status']) => void;
  onSubtaskStatusChange: (taskId: string, subtaskId: string, status: SubTask['status']) => void;
  onSubtaskDelete?: (taskId: string, subtaskId: string) => void;
  onSubtaskEdit?: (taskId: string, subtaskId: string) => void;
  isSaved: boolean;
  goalReviews?: GoalReview[];
  isReviewLoading?: boolean;
  onSave?: (taskId: string) => void;
  onEdit?: (taskId: string) => void;
  isSaving?: boolean;
}

export const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  task,
  isOpen,
  onClose,
  onAddSubtask,
  onStatusChange,
  onSubtaskStatusChange,
  onSubtaskDelete,
  onSubtaskEdit,
  isSaved,
  goalReviews = [],
  isReviewLoading,
  onSave,
  onEdit,
  isSaving
}) => {
  const [newSubtask, setNewSubtask] = useState<Omit<SubTask, 'id'>>({
    title: '',
    description: '',
    deadline: new Date().toISOString().split('T')[0], // Set default deadline to today
    priority: 'Medium',
    status: 'Pending'
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!newSubtask.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (newSubtask.title.length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    }
    
    if (newSubtask.description && newSubtask.description.length > 255) {
      newErrors.description = 'Description must be less than 255 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddSubtask = async () => {
    if (!task || !validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onAddSubtask(task.id, newSubtask);
      
      // Reset form
      setNewSubtask({
        title: '',
        description: '',
        deadline: new Date().toISOString().split('T')[0], // Reset deadline to today
        priority: 'Medium',
        status: 'Pending'
      });
      setShowAddForm(false);
      setErrors({});
    } catch (error) {
      console.error('Error adding subtask:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof Omit<SubTask, 'id'>, value: string) => {
    setNewSubtask(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setNewSubtask({
      title: '',
      description: '',
      deadline: new Date().toISOString().split('T')[0], // Reset deadline to today
      priority: 'Medium',
      status: 'Pending'
    });
    setErrors({});
  };

  const getCompletionPercentage = () => {
    if (!task || task.subtasks.length === 0) return 0;
    const completed = task.subtasks.filter(subtask => subtask.status === 'Completed').length;
    return Math.round((completed / task.subtasks.length) * 100);
  };

  const getSubtaskCounts = () => {
    if (!task) return { total: 0, completed: 0, inProgress: 0, pending: 0 };
    
    return {
      total: task.subtasks.length,
      completed: task.subtasks.filter(st => st.status === 'Completed').length,
      inProgress: task.subtasks.filter(st => st.status === 'In Progress').length,
      pending: task.subtasks.filter(st => st.status === 'Pending').length
    };
  };

  if (!task) return null;

  const completionPercentage = getCompletionPercentage();
  const subtaskCounts = getSubtaskCounts();

  return (
    <Model modalSize="lg" showModal={isOpen} onClose={onClose}>
      <div className="task-details-modal">
        <div className="modal-header">
          <div className="task-header">
            <div className="task-title">
              <Typography type="heading4" text={task.title} />
            </div>
            {isSaved && (
              <select
                value={task.status}
                onChange={(e) => onStatusChange(task.id, e.target.value as Task['status'])}
                className={`status-dropdown status-${task.status.toLowerCase().replace(' ', '-')}`}
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            )}
          </div>
          
          <Typography type="base_regular" text={task.description} className="task-description" />
          
          {task.subtasks.length > 0 && (
            <div className="task-progress">
              <div className="progress-header">
                <Typography type="base_medium" text="Progress Overview" />
                <Typography type="small_regular" text={`${completionPercentage}% Complete`} className="progress-percentage" />
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
              <div className="progress-stats">
                <div className="stat-item">
                  <span className="stat-number">{subtaskCounts.total}</span>
                  <span className="stat-label">Total</span>
                </div>
                <div className="stat-item completed">
                  <span className="stat-number">{subtaskCounts.completed}</span>
                  <span className="stat-label">Completed</span>
                </div>
                <div className="stat-item in-progress">
                  <span className="stat-number">{subtaskCounts.inProgress}</span>
                  <span className="stat-label">In Progress</span>
                </div>
                <div className="stat-item pending">
                  <span className="stat-number">{subtaskCounts.pending}</span>
                  <span className="stat-label">Pending</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-content">
          <div className="subtasks-section">
            <div className="subtasks-header">
              <Typography type="heading5" text="Subtasks" />
              {!isSaved && (
                <Button
                  type="secondary"
                  size="sm"
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="add-subtask-btn"
                >
                  {showAddForm ? 'Cancel' : 'Add Subtask'}
                </Button>
              )}
            </div>

            {showAddForm && (
              <div className="add-subtask-form">
                <div className="form-header">
                  <Typography type="base_medium" text="Add New Subtask" />
                </div>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="subtask-title">Title *</label>
                    <input
                      id="subtask-title"
                      type="text"
                      placeholder="Enter subtask title"
                      value={newSubtask.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className={errors.title ? 'error' : ''}
                      maxLength={255}
                    />
                    {errors.title && <span className="error-message">{errors.title}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="subtask-priority">Priority</label>
                    <select
                      id="subtask-priority"
                      value={newSubtask.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                    >
                      <option value="High">High Priority</option>
                      <option value="Medium">Medium Priority</option>
                      <option value="Low">Low Priority</option>
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="subtask-description">Description</label>
                    <textarea
                      id="subtask-description"
                      placeholder="Enter subtask description (optional)"
                      value={newSubtask.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className={errors.description ? 'error' : ''}
                      rows={3}
                      maxLength={255}
                    />
                    <div className="char-count">
                      {newSubtask.description.length}/255
                    </div>
                    {errors.description && <span className="error-message">{errors.description}</span>}
                  </div>
                </div>

                <div className="form-actions">
                  <Button 
                    type="secondary" 
                    size="sm" 
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    size="sm" 
                    onClick={handleAddSubtask}
                    disabled={isSubmitting}
                    className="save-btn"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Subtask'}
                  </Button>
                </div>
              </div>
            )}

            <div className="subtasks-list">
              {task.subtasks.length > 0 ? (
                task.subtasks.map(subtask => (
                  <div key={subtask.id} className="subtask-item">
                    <div className="subtask-header">
                      <div className="subtask-title">
                        <div className={`status-dot status-${subtask.status.toLowerCase().replace(' ', '-')}`} />
                        <Typography type="base_regular" text={subtask.title} className='subtask-title-text' />
                      </div>
                      
                    </div>
                    
                    {subtask.description && (
                      <Typography type="small_regular" text={subtask.description} className="subtask-description" />
                    )}
                    
                    <div className="subtask-footer">
                      <span className={`priority-tag priority-${subtask.priority.toLowerCase()}`}>
                        {subtask.priority}
                      </span>
                      <span className='subtask-actions-container'>
                        <div className="subtask-actions">
                          {isSaved && (
                            <select
                              value={subtask.status}
                              onChange={(e) => onSubtaskStatusChange(task.id, subtask.id, e.target.value as SubTask['status'])}
                              className={`status-dropdown status-${subtask.status.toLowerCase().replace(' ', '-')}`}
                            >
                              <option value="Pending">Pending</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Completed">Completed</option>
                            </select>
                          )}
                          {!isSaved && onSubtaskDelete && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSubtaskDelete(task.id, subtask.id);
                              }}
                              className="delete-subtask-btn"
                              title="Delete subtask"
                            >
                              <img src="/icons/delete.svg" alt="Delete" />
                            </button>
                          )}
                          {!isSaved && onSubtaskEdit && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSubtaskEdit(task.id, subtask.id);
                            }}
                            className="edit-subtask-btn"
                            title="Edit subtask"
                          >
                            <img src="/icons/edit-button.svg" alt="Edit" className='edit-subtask-btn-icon' />
                          </button>
                          )}
                        </div>
                      </span>
                      <Typography type="small_regular" text={`Due: ${new Date(subtask.deadline).toLocaleDateString()}`} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <Typography type="base_regular" text="No subtasks yet. Add your first subtask to get started!" />
                </div>
              )}
            </div>
          </div>

          {/* Goal Reviews Section */}
          <div className="goal-reviews-section">
            <Typography type="heading5" text="Goal Reviews" className="reviews-title" />
            {isReviewLoading ? (
              <div className="review-loading">
                <Typography type="base_regular" text="Loading reviews..." />
              </div>
            ) : goalReviews.length > 0 ? (
              <div className="goal-reviews-list">
                {goalReviews
                  .sort((a, b) => new Date(b.reviewDate).getTime() - new Date(a.reviewDate).getTime())
                  .map((review) => (
                    <div key={review.id} className="goal-review-item">
                      <div className="review-header">
                        <div className="review-info">
                          <Typography type="base_medium" text={`${review.reviewerRole} Review`} className="review-type" />
                          <div className="review-rating">
                            {[...Array(5)].map((_, index) => (
                              <span key={index} className={`star ${index < review.rating ? 'filled' : 'empty'}`}>
                                ★
                              </span>
                            ))}
                            <span className="rating-text">({review.rating}/5)</span>
                          </div>
                        </div>
                        <div className="review-meta">
                          <Typography type="small_medium" text={review.reviewerName} className="reviewer-name" />
                          <Typography type="small_regular" text={new Date(review.reviewDate).toLocaleDateString()} className="review-date" />
                        </div>
                      </div>
                      <div className="review-content">
                        <Typography type="base_regular" text={review.reviewText} className="review-text" />
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="no-review">
                <Typography type="base_regular" text="No reviews available for this goal yet." className="no-review-text" />
              </div>
            )}
          </div>

          {isSaved && task.status === 'Completed' && task.reviews.length > 0 && (
            <div className="task-reviews">
              <Typography type="heading5" text="Task Reviews" className="reviews-title" />
              <div className="reviews-list">
                {task.reviews.map((review) => (
                  <div key={review.id} className="review-item">
                    <div className="review-header">
                      <div className="review-rating">
                        {[...Array(5)].map((_, index) => (
                          <span key={index} className={`star ${index < review.rating ? 'filled' : ''}`}>
                            ★
                          </span>
                        ))}
                      </div>
                      <div className="review-meta">
                        <Typography type="small_medium" text={review.reviewedBy} />
                        <Typography type="small_regular" text={new Date(review.reviewedAt).toLocaleDateString()} />
                      </div>
                    </div>
                    <Typography type="base_regular" text={review.comment} className="review-comment" />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Save/Edit buttons for the entire goal */}
          {task && onSave && onEdit && (
            <div className="modal-actions">
              {!isSaved ? (
                <Button
                  type="primary"
                  size="base"
                  onClick={() => onSave(task.id)}
                  disabled={isSaving}
                  className="modal-save-btn"
                >
                  {isSaving ? 'Saving...' : 'Save All Subtasks'}
                </Button>
              ) : (
                <Button
                  type="secondary"
                  size="base"
                  onClick={() => onEdit(task.id)}
                  className="modal-edit-btn"
                >
                  Edit Subtasks
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Model>
  );
}; 